import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MailX, Search, ArrowLeft, RefreshCcw, Copy, Globe, AlertOctagon, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Parent Dashboard · Email Unsubscribes
 *
 * Lists every email that opted out of Kairosa/Jay Project outreach. Data
 * flows in from `/api/email/unsubscribe/{token}` (public link inside every
 * outreach email) and from Gmail's one-click unsubscribe POST handler.
 * Dispatcher checks this list before every send — no double-messaging.
 */

const SOURCE_LABELS = {
  email_link: "Email link",
  one_click_post: "Gmail 1-click",
  manual: "Manual",
  bounce_sync: "Hard bounce (SMTP)",
  bounce_dsn: "Hard bounce (DSN)",
};

export default function UnsubscribesPage() {
  const [tab, setTab] = useState("unsubs");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [bounces, setBounces] = useState([]);
  const [bouncesTotal, setBouncesTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [rescanBusy, setRescanBusy] = useState(false);

  useEffect(() => {
    setLoading(true);
    const p1 = api.get("/admin/unsubscribes", { params: { q: q || undefined } })
      .then(({ data }) => { setItems(data.items || []); setTotal(data.total || 0); });
    const p2 = api.get("/admin/bounces", { params: { limit: 200 } })
      .then(({ data }) => { setBounces(data.items || []); setBouncesTotal(data.total || 0); });
    Promise.all([p1, p2])
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load list"))
      .finally(() => setLoading(false));
  }, [q, tick]);

  const rescanBounces = async () => {
    setRescanBusy(true);
    try {
      const { data } = await api.post("/admin/bounces/rescan");
      if (data.ok) {
        toast.success(`Scanned inbox · ${data.processed} new bounce${data.processed === 1 ? "" : "s"}`);
        setTick((t) => t + 1);
      } else {
        toast.error(`Scan failed: ${data.reason || "unknown"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rescan failed");
    } finally { setRescanBusy(false); }
  };

  const copyList = () => {
    const csv = items.map((r) => r.email).join("\n");
    navigator.clipboard.writeText(csv);
    toast.success(`${items.length} emails copied`);
  };

  const kpi = useMemo(() => {
    const byDay = {};
    for (const r of items) {
      const d = (r.unsubscribed_at || "").slice(0, 10);
      byDay[d] = (byDay[d] || 0) + 1;
    }
    const today = new Date().toISOString().slice(0, 10);
    const hardBounces = items.filter((r) => (r.source || "").startsWith("bounce_")).length;
    return {
      total,
      today: byDay[today] || 0,
      oneClick: items.filter((r) => r.source === "one_click_post").length,
      hardBounces,
    };
  }, [items, total]);

  return (
    <div className="space-y-8" data-testid="unsubscribes-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-primary mb-2">Parent dashboard · email</div>
          <h1 className="font-display text-4xl leading-tight">Unsubscribes.</h1>
          <p className="mt-2 text-muted-foreground max-w-2xl">
            Every recipient who tapped the <b>Unsubscribe</b> link inside an outreach or automation email.
            Kairosa's dispatcher skips these addresses on every future send — no manual scrubbing needed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-full gap-1.5" onClick={rescanBounces} disabled={rescanBusy} data-testid="unsub-rescan-bounces">
            <Zap className="h-3.5 w-3.5 text-amber-500" /> {rescanBusy ? "Scanning…" : "Scan bounces"}
          </Button>
          <Button variant="outline" className="rounded-full gap-1.5" onClick={() => setTick((t) => t + 1)} data-testid="unsub-refresh">
            <RefreshCcw className="h-3.5 w-3.5" /> Refresh
          </Button>
          <Button variant="outline" className="rounded-full gap-1.5" onClick={copyList} disabled={items.length === 0} data-testid="unsub-copy">
            <Copy className="h-3.5 w-3.5" /> Copy list
          </Button>
          <Link to="/app/admin"><Button variant="ghost" className="rounded-full gap-1.5"><ArrowLeft className="h-3.5 w-3.5" /> Parent dashboard</Button></Link>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-xl border border-border bg-card p-4" data-testid="unsub-kpi-total">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            <MailX className="h-3.5 w-3.5 text-destructive" /> Total unsubscribes
          </div>
          <div className="font-display text-3xl mt-2 tabular-nums">{kpi.total.toLocaleString()}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4" data-testid="unsub-kpi-today">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            <MailX className="h-3.5 w-3.5 text-amber-500" /> Today
          </div>
          <div className="font-display text-3xl mt-2 tabular-nums">{kpi.today}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4" data-testid="unsub-kpi-one-click">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            <Globe className="h-3.5 w-3.5 text-primary" /> Gmail 1-click
          </div>
          <div className="font-display text-3xl mt-2 tabular-nums">{kpi.oneClick}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4" data-testid="unsub-kpi-bounces">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            <AlertOctagon className="h-3.5 w-3.5 text-orange-500" /> Hard bounces
          </div>
          <div className="font-display text-3xl mt-2 tabular-nums">{kpi.hardBounces}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="inline-flex rounded-full border border-border bg-secondary/40 p-1">
        <button
          onClick={() => setTab("unsubs")}
          data-testid="unsub-tab-unsubs"
          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] transition-colors ${tab === "unsubs" ? "bg-destructive text-white" : "text-muted-foreground hover:text-foreground"}`}
        >
          <MailX className="h-3.5 w-3.5" /> Unsubscribes ({total})
        </button>
        <button
          onClick={() => setTab("bounces")}
          data-testid="unsub-tab-bounces"
          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] transition-colors ${tab === "bounces" ? "bg-orange-500 text-white" : "text-muted-foreground hover:text-foreground"}`}
        >
          <AlertOctagon className="h-3.5 w-3.5" /> Bounces ({bouncesTotal})
        </button>
      </div>

      {/* Search — only for unsubs list */}
      {tab === "unsubs" && (
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email…"
            className="pl-9 rounded-full"
            data-testid="unsub-search"
          />
        </div>
      )}

      {tab === "unsubs" && (
        <div className="rounded-xl border border-border overflow-hidden bg-card">
          <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            <div className="col-span-5">Email</div>
            <div className="col-span-2">Source</div>
            <div className="col-span-3">Unsubscribed</div>
            <div className="col-span-2">Tenant</div>
          </div>
          {loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground" data-testid="unsub-empty">
              No unsubscribes yet. Anyone who taps the unsubscribe link — or gets returned by Gmail as a hard bounce — will show up here.
            </div>
          ) : (
            items.map((r) => {
              const isBounce = (r.source || "").startsWith("bounce_");
              return (
                <div
                  key={r.email}
                  data-testid={`unsub-row-${r.email}`}
                  className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border last:border-0 items-center text-sm hover:bg-secondary/40 transition"
                >
                  <div className="col-span-5 min-w-0">
                    <div className="font-medium truncate">{r.email}</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {isBounce && r.bounce_message ? r.bounce_message : (r.ip ? `IP ${r.ip}` : "—")}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <span className={"inline-flex items-center rounded-full px-2 py-0.5 text-[11px] " + (isBounce ? "bg-orange-500/15 text-orange-600" : "bg-secondary text-foreground/70")}>
                      {SOURCE_LABELS[r.source] || r.source || "—"}
                    </span>
                  </div>
                  <div className="col-span-3 text-[11px] text-muted-foreground tabular-nums">
                    {r.unsubscribed_at ? new Date(r.unsubscribed_at).toLocaleString() : "—"}
                  </div>
                  <div className="col-span-2 text-[11px] text-muted-foreground truncate">
                    {r.tenant_id ? r.tenant_id.slice(-6) : "—"}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {tab === "bounces" && (
        <div className="rounded-xl border border-border overflow-hidden bg-card">
          <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            <div className="col-span-4">Email</div>
            <div className="col-span-1 text-center">Hard</div>
            <div className="col-span-1">Code</div>
            <div className="col-span-2">Source</div>
            <div className="col-span-2">When</div>
            <div className="col-span-2">Message</div>
          </div>
          {loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : bounces.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground" data-testid="bounces-empty">
              No bounces yet. When Gmail rejects a send (or returns a DSN later), the recipient will show up here and be auto-unsubscribed if it's a hard 5.x.x failure.
            </div>
          ) : (
            bounces.map((b, i) => (
              <div
                key={i}
                data-testid={`bounce-row-${b.email}`}
                className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border last:border-0 items-center text-sm hover:bg-secondary/40 transition"
              >
                <div className="col-span-4 min-w-0">
                  <div className="font-medium truncate">{b.email}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{b.subject || "—"}</div>
                </div>
                <div className="col-span-1 text-center">
                  {b.hard ? (
                    <span className="inline-flex items-center rounded-full bg-destructive/15 text-destructive px-2 py-0.5 text-[10px]">HARD</span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-secondary text-foreground/60 px-2 py-0.5 text-[10px]">soft</span>
                  )}
                </div>
                <div className="col-span-1 tabular-nums text-xs">{b.code || "—"}</div>
                <div className="col-span-2 text-[11px] text-muted-foreground uppercase tracking-[0.14em]">{(b.source || "").replace("_", " ")}</div>
                <div className="col-span-2 text-[11px] text-muted-foreground tabular-nums">
                  {b.at ? new Date(b.at).toLocaleString() : "—"}
                </div>
                <div className="col-span-2 text-[11px] text-muted-foreground truncate" title={b.message}>
                  {b.message || "—"}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
